import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';

const SplashScreen = ({ onEnter }) => {
  const [isExiting, setIsExiting] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  const handleEnter = () => {
    setIsExiting(true);
    setTimeout(onEnter, 800);
  };

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[99999] bg-[#0a1610] flex items-center justify-center overflow-hidden"
        >
          {/* Premium gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1610] via-[#0d1f16] to-[#0a1610]" />
          
          {/* Animated background orbs - more subtle */}
          <motion.div
            className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(20,90,58,0.15) 0%, transparent 70%)',
            }}
            animate={{ 
              x: [0, 50, 0],
              y: [0, 30, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(195,163,101,0.08) 0%, transparent 70%)',
            }}
            animate={{ 
              x: [0, -40, 0],
              y: [0, -20, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Language Toggle - Top right */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2 }}
            onClick={toggleLanguage}
            className="absolute top-6 right-6 px-3 py-1.5 text-xs tracking-widest uppercase text-[#8BA89A] hover:text-[#C3A365] transition-colors border border-[#1E2F26] hover:border-[#C3A365]/30"
          >
            {language === 'fr' ? 'EN' : 'FR'}
          </motion.button>

          {/* Content */}
          <div className="relative z-10 text-center px-8">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 1.2, ease: 'easeOut' }}
            >
              <h1 
                className="brand-logo text-6xl sm:text-7xl md:text-8xl text-[#f5f5f0] tracking-tight"
                style={{ fontWeight: 300 }}
              >
                habitué
              </h1>
              <motion.div
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1, duration: 1, ease: 'easeInOut' }}
                className="h-[1px] bg-gradient-to-r from-transparent via-[#C3A365]/60 to-transparent mx-auto max-w-[200px] mt-6"
              />
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              className="text-[#8BA89A] text-base sm:text-lg mt-8 mb-14 tracking-wide font-light"
            >
              {t('splash_tagline')}
            </motion.p>

            {/* Enter Button - More premium */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.6 }}
              onClick={handleEnter}
              className="group relative px-14 py-4 bg-transparent border border-[#C3A365]/40 text-[#C3A365] font-light tracking-[0.3em] uppercase text-xs overflow-hidden hover:border-[#C3A365] transition-colors duration-500"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.span
                className="absolute inset-0 bg-gradient-to-r from-[#C3A365] to-[#D4B576]"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.4 }}
              />
              <span className="relative z-10 flex items-center gap-4 group-hover:text-[#0a1610] transition-colors duration-300">
                {t('splash_enter')}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </span>
            </motion.button>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.2, duration: 0.8 }}
              className="text-[#5a6e62] text-[10px] mt-20 tracking-[0.25em] uppercase"
            >
              {t('splash_footer')}
            </motion.p>
          </div>

          {/* Premium corner accents */}
          <div className="absolute top-8 left-8 w-20 h-20">
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#C3A365]/30 to-transparent" />
            <div className="absolute top-0 left-0 w-[1px] h-full bg-gradient-to-b from-[#C3A365]/30 to-transparent" />
          </div>
          <div className="absolute top-8 right-8 w-20 h-20">
            <div className="absolute top-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#C3A365]/30 to-transparent" />
            <div className="absolute top-0 right-0 w-[1px] h-full bg-gradient-to-b from-[#C3A365]/30 to-transparent" />
          </div>
          <div className="absolute bottom-8 left-8 w-20 h-20">
            <div className="absolute bottom-0 left-0 w-full h-[1px] bg-gradient-to-r from-[#C3A365]/30 to-transparent" />
            <div className="absolute bottom-0 left-0 w-[1px] h-full bg-gradient-to-t from-[#C3A365]/30 to-transparent" />
          </div>
          <div className="absolute bottom-8 right-8 w-20 h-20">
            <div className="absolute bottom-0 right-0 w-full h-[1px] bg-gradient-to-l from-[#C3A365]/30 to-transparent" />
            <div className="absolute bottom-0 right-0 w-[1px] h-full bg-gradient-to-t from-[#C3A365]/30 to-transparent" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
